import { ArrowDown } from '@/assets/icons/info-icons/arrowDown';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { themeVar } from '@/styles/theme';

interface ShowTextProps {
  children: React.ReactNode;
  maxHeight?: number;
}

const ShowText = ({ children, maxHeight = 96 }: ShowTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isShowMore, setIsShowMore] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      setIsOverflowing(el.scrollHeight > maxHeight);
    }
  }, [children, maxHeight]);

  return (
    <Wrapper>
      <ContentWrapper ref={containerRef} isOverflowing={isOverflowing} isShowMore={isShowMore} maxHeight={maxHeight}>
        {children}
      </ContentWrapper>
      {isOverflowing && (
        <ShowButton onClick={() => setIsShowMore(!isShowMore)}>
          Show {isShowMore ? 'less' : 'more'}
          {isShowMore ? (
            <svg width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.940002 5.47L4 2.418L7.06 5.47L8 4.53L4 0.530001L-3.70319e-08 4.53L0.940002 5.47Z" fill={themeVar('font.action')} />
            </svg>
          ) : (
            <ArrowDown fill={themeVar('font.action')} />
          )}
        </ShowButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div<{ isOverflowing: boolean; isShowMore: boolean; maxHeight: number }>`
  width: 100%;
  font-size: 16px;
  color: ${themeVar('font.base')};
  overflow: hidden;
  max-height: ${({ isShowMore, maxHeight }) => (isShowMore ? 'none' : `${maxHeight}px`)};
  position: relative;
  transition: max-height 0.3s ease;

  ${({ isOverflowing, isShowMore }) =>
    isOverflowing &&
    !isShowMore &&
    `
    -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0.7) 70%, rgba(0, 0, 0, 0.3) 90%, rgba(0, 0, 0, 0) 100%);
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 50%, rgba(0, 0, 0, 0.7) 70%, rgba(0, 0, 0, 0.3) 90%, rgba(0, 0, 0, 0) 100%);
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
  `}
`;

const ShowButton = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  font-weight: 600;
  font-size: 13px;
  line-height: 20px;
  color: ${themeVar('font.action')};
  cursor: pointer;
  user-select: none;
  margin-top: 8px;
`;

export default ShowText;
